import { Me } from "@bandstand/api/Me";
import { action, computed, observable } from "mobx";

import { ShareError } from "../share/ShareError";
import type { ShareClient } from "../share/ShareClient";
import { saveSettings } from "./savedSettings";

const KEY = "bandstand.account";

/** Who's signed in on this device. */
export type Account = Me["account"];

/**
 * Signing this device in to an account on the share server, with a code sent by email. The account is kept on the
 * device too, so the app knows who's signed in without a connection.
 */
export class AccountStore {
    @observable accessor account: Account | undefined = undefined;

    constructor(private readonly share: ShareClient) {
        try {
            const saved: unknown = JSON.parse(localStorage.getItem(KEY) ?? "null");
            const account = Me.shape.account.safeParse(saved);

            if (account.success) this.account = account.data;
        } catch {
            // Nobody's signed in, as far as this device knows.
        }
    }

    @computed get signedIn(): boolean {
        return this.account !== undefined;
    }

    /** Emails a sign-in code to an address, which only works on this device. */
    async requestCode(email: string): Promise<void> {
        await this.share.request("POST", "/sign-in/code", { email });
    }

    /** Signs this device in with the code from the email. A name is only needed the first time. */
    async signIn(email: string, code: string, name?: string): Promise<void> {
        const me = await this.share.request("POST", "/sign-in", { email, code, name: name?.trim() || undefined }, Me);

        this.setAccount(me.account);
    }

    /** Asks the share server who's signed in, in case that changed elsewhere, like this device being signed out. */
    async refresh(): Promise<void> {
        if (!this.account) return;

        try {
            this.setAccount((await this.share.request("GET", "/me", undefined, Me)).account);
        } catch (error) {
            if (error instanceof ShareError && error.status === 401) this.setAccount(undefined);
        }
    }

    /** Signs this device out on the share server, and forgets the account here. */
    async signOut(): Promise<void> {
        await this.share.request("DELETE", `/devices/${await this.share.deviceKey()}`);
        this.setAccount(undefined);
    }

    @action
    private setAccount(account: Account | undefined): void {
        this.account = account;

        saveSettings(KEY, account ?? null);
    }
}
